import { Cast, Close, Dashboard, DisplaySettings, LocalOffer, People, Warning } from '@mui/icons-material'
import PermMediaIcon from '@mui/icons-material/PermMedia'
import QueuePlayNextIcon from '@mui/icons-material/QueuePlayNext'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import { Alert, Box, Chip, Collapse, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography, useMediaQuery } from '@mui/material'
import MuiDrawer from '@mui/material/Drawer'
import { CSSObject, styled, Theme, useTheme } from '@mui/material/styles'
import { ReactElement, useEffect, useState } from 'react'
import { Link, Outlet, useLocation } from 'react-router-dom'
import { useThemeMode } from '../Context/ThemeProvider'
import { useAppSelector } from '../store/hooks'
import UserBar from './AppBarUser'

export const drawerWidth = 240

type MenuItem = { text: string; path: string; icon: ReactElement; role: string[]; badge?: string }

const allMenuItems: MenuItem[] = [
  { text: 'Dashboard',  path: '/dashboard', icon: <Dashboard        sx={{ fontSize: 17 }} />, role: ['USER', 'ADMIN', 'SUPER_ADMIN'] },
  { text: 'Playlists',  path: '/playlists', icon: <QueuePlayNextIcon sx={{ fontSize: 17 }} />, role: ['USER', 'ADMIN', 'SUPER_ADMIN'], badge: 'Ativo' },
  { text: 'Mídia',      path: '/media',     icon: <PermMediaIcon    sx={{ fontSize: 17 }} />, role: ['USER', 'ADMIN', 'SUPER_ADMIN'] },
  { text: 'Produtos',   path: '/produtos',  icon: <ShoppingCartIcon sx={{ fontSize: 17 }} />, role: ['USER', 'ADMIN', 'SUPER_ADMIN'] },
  { text: 'Templates',  path: '/templates', icon: <DisplaySettings  sx={{ fontSize: 17 }} />, role: ['USER', 'ADMIN', 'SUPER_ADMIN'] },
  { text: 'Displays',   path: '/displays',  icon: <Cast             sx={{ fontSize: 17 }} />, role: ['USER', 'ADMIN', 'SUPER_ADMIN'] },
  { text: 'Usuários',   path: '/users',     icon: <People           sx={{ fontSize: 17 }} />, role: ['ADMIN', 'SUPER_ADMIN'] },
  { text: 'Planos',     path: '/plans',     icon: <LocalOffer       sx={{ fontSize: 17 }} />, role: ['ADMIN', 'SUPER_ADMIN'] },
]

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.enteringScreen }),
  overflowX: 'hidden',
})
const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.leavingScreen }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: { width: `calc(${theme.spacing(8)} + 1px)` },
})

const LogoIcon = () => (
  <svg viewBox="0 0 28 28" fill="none" style={{ width: 26, height: 26, flexShrink: 0 }}>
    <rect x="2" y="8" width="6" height="12" rx="2" fill="#14b8a0" opacity="0.9" />
    <rect x="11" y="4" width="6" height="20" rx="2" fill="#14b8a0" />
    <rect x="20" y="11" width="6" height="10" rx="2" fill="#14b8a0" opacity="0.7" />
  </svg>
)

const UserDrawer = () => {
  const location = useLocation()
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const tk = theme.tokens as any
  const [open, setOpen] = useState(false)
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const user = useAppSelector(s => s.auth.user)
  const [menuItems, setMenuItems] = useState<MenuItem[]>([])
  const { isDarkMode } = useThemeMode()
  const [warningDismissed, setWarningDismissed] = useState(false)

  const sidebarBg   = isDark ? '#111c22' : '#f8fafb'
  const sidebarBdr  = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.07)'
  const textMuted   = isDark ? '#4a6a78' : '#8aaabb'
  const textSec     = isDark ? '#7fa3b0' : '#4a7080'
  const textPri     = isDark ? '#e8f0f2' : '#0f2b35'
  const hoverBg     = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'
  const activeBg    = isDark
    ? 'linear-gradient(135deg, #0d5a5e 0%, #0a4a4e 100%)'
    : 'linear-gradient(135deg, #e6f7f5 0%, #d4f0ec 100%)'
  const activeShadow = isDark
    ? '0 4px 18px rgba(14,94,99,0.4), inset 0 1px 0 rgba(255,255,255,0.08)'
    : '0 2px 8px rgba(13,148,136,0.15)'
  const activeIconColor = isDark ? '#14b8a0' : '#0d9488'
  const iconColor   = isDark ? '#4a6a78' : '#7fa3b0'

  const Drawer = styled(MuiDrawer)(({ open: o }) => ({
    width: drawerWidth, flexShrink: 0, whiteSpace: 'nowrap', boxSizing: 'border-box', border: 'none',
    ...(o && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': { ...openedMixin(theme), backgroundColor: sidebarBg, borderRight: `1px solid ${sidebarBdr}` },
    }),
    ...(!o && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': { ...closedMixin(theme), backgroundColor: sidebarBg, borderRight: `1px solid ${sidebarBdr}` },
    }),
  }))

  useEffect(() => {
    if (!user) return
    setMenuItems(allMenuItems.filter(item =>
      item.role.length === 0 || item.role.includes(user.role)
    ))
  }, [user])

  const isEditMode = /\/edit\//.test(location.pathname)

  useEffect(() => {
    if (location.pathname.includes('edit')) setOpen(false)
    else if (!isMobile) setOpen(true)
  }, [location, isMobile])

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      {!isEditMode && (
        <Drawer variant={isMobile ? 'temporary' : 'permanent'} open={open} anchor="left">

          {/* Logo */}
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.25,
            px: 2, pt: 2.5, pb: 2.5,
            bgcolor: sidebarBg,
            minHeight: 64,
          }}>
            <LogoIcon />
            {open && (
              <Typography sx={{ fontSize: 17, fontWeight: 700, color: textPri, letterSpacing: '-0.3px' }}>
                Midiaindoor
              </Typography>
            )}
          </Box>

          {/* Itens */}
          <List sx={{ flexGrow: 1, bgcolor: sidebarBg, px: open ? 1.25 : 0.75, py: 0.5 }}>
            {menuItems.map(item => {
              const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/')
              return (
                <ListItem key={item.text} disablePadding sx={{ display: 'block', mb: 0.5 }}>
                  <ListItemButton
                    component={Link} to={item.path}
                    onClick={() => { if (isMobile) { (document.activeElement as HTMLElement)?.blur(); setOpen(false) } }}
                    sx={{
                      minHeight: 40, px: 1.5, borderRadius: '8px',
                      justifyContent: open ? 'initial' : 'center',
                      color: isActive ? (isDark ? '#fff' : '#0f2b35') : textSec,
                      background: isActive ? activeBg : 'transparent',
                      boxShadow: isActive ? activeShadow : 'none',
                      transition: 'background 0.15s, box-shadow 0.15s',
                      '&:hover': { background: isActive ? activeBg : hoverBg },
                    }}
                  >
                    <ListItemIcon sx={{
                      minWidth: 0, mr: open ? 1.5 : 'auto', justifyContent: 'center',
                      color: isActive ? activeIconColor : iconColor,
                      transition: 'color 0.15s',
                    }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText sx={{ opacity: open ? 1 : 0, m: 0 }} primary={
                      <Typography sx={{ fontSize: 14, fontWeight: isActive ? 600 : 500, color: 'inherit', lineHeight: 1 }}>
                        {item.text}
                      </Typography>
                    } />
                    {open && item.badge && (
                      <Chip label={item.badge} size="small" sx={{
                        fontSize: 10, fontWeight: 600, height: 18, ml: 0.5,
                        bgcolor: isDark ? 'rgba(20,184,160,0.18)' : 'rgba(13,148,136,0.12)',
                        color: isDark ? '#14b8a0' : '#0d9488',
                        border: 'none', borderRadius: '20px',
                        '& .MuiChip-label': { px: '7px', py: '2px' },
                      }} />
                    )}
                  </ListItemButton>
                </ListItem>
              )
            })}

            {/* Help — rodapé */}
            <ListItem disablePadding sx={{ display: 'block', mt: 'auto', mb: 0.5 }}>
              <ListItemButton component={Link} to="/faq"
                sx={{
                  minHeight: 40, px: 1.5, borderRadius: '8px',
                  justifyContent: open ? 'initial' : 'center',
                  color: textSec, '&:hover': { background: hoverBg },
                }}>
                <ListItemIcon sx={{ minWidth: 0, mr: open ? 1.5 : 'auto', justifyContent: 'center', color: iconColor }}>
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                </ListItemIcon>
                <ListItemText sx={{ opacity: open ? 1 : 0, m: 0 }} primary={
                  <Typography sx={{ fontSize: 14, fontWeight: 500, color: 'inherit', lineHeight: 1 }}>Ajuda</Typography>
                } />
              </ListItemButton>
            </ListItem>
          </List>

          {/* Versão e último deploy */}
          {open && (
            <Box sx={{ px: 2, pb: 1, pt: 0.5 }}>
              <Typography sx={{ fontSize: 10, color: textMuted, letterSpacing: '0.03em', lineHeight: 1.6 }}>
                v1.1.0 · Deploy 28/05/2026
              </Typography>
            </Box>
          )}

          {/* Toggle abrir/fechar no rodapé */}
          <Box sx={{ bgcolor: sidebarBg, borderTop: `1px solid ${sidebarBdr}`, px: 1.25, py: 1 }}>
            <ListItemButton onClick={() => setOpen(o => !o)} sx={{
              borderRadius: '8px', minHeight: 36, justifyContent: open ? 'flex-end' : 'center',
              px: 1.5, '&:hover': { bgcolor: hoverBg },
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={textMuted} strokeWidth="2"
                style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                <polyline points="9 18 15 12 9 6" />
              </svg>
              {open && <Typography sx={{ fontSize: 12, color: textMuted, ml: 0.75 }}>Recolher</Typography>}
            </ListItemButton>
          </Box>
        </Drawer>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', minWidth: 0, bgcolor: 'background.default' }}>
        <UserBar open={open} isEditMode={isEditMode} onMenuClick={() => setOpen(o => !o)} />
        {user?.warning && (
          <Collapse in={!warningDismissed}>
            <Alert
              severity="warning"
              icon={<Warning fontSize="small" />}
              action={
                <IconButton size="small" onClick={() => setWarningDismissed(true)}>
                  <Close fontSize="small" />
                </IconButton>
              }
              sx={{ borderRadius: 0, fontSize: 13, py: 0.75 }}
            >
              {user.warning}
            </Alert>
          </Collapse>
        )}
        <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  )
}

export default UserDrawer
